import { useCallback, useEffect, useMemo, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { styled } from '@mui/material/styles';
import {
  Box,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import { NoDataFound } from "../../components/common-components/no-data";

const StyledTable = styled(Table)(({ theme }) => ({
    '& th': {
        padding: '12px 10px !important',
        fontWeight: '700',
        fontSize: '11px',
        color: '#5f5a54',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        background: '#fafbff',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        borderBottom: '1px solid #ebe8e1',
    },
    '& td': {
        padding: '11px 10px !important',
        borderBottom: '1px solid #f0ede8',
        fontSize: '13px',
        color: '#2f2b27',
    },
    '& tbody tr:hover': {
      background: 'rgba(25, 118, 210, 0.05)',
    },
  }));

const RowTypes = {
  ROW: 'row',
};

const DraggableTableRow = ({ row, index, moveRow, columns, disableReorder, dragHandleColumnIndex }) => {
  const [, drag] = useDrag({
    type: RowTypes.ROW,
    item: { index },
    canDrag: !disableReorder,
  });

  const [, drop] = useDrop({
    accept: RowTypes.ROW,
    hover(item) {
      // console.log('item hover', item)
    },
    drop(item) {
      if (!item || disableReorder) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const usesHandle = Number.isInteger(dragHandleColumnIndex);

  return (
    <TableRow ref={usesHandle ? drop : (node) => drag(drop(node))}>
      {columns.map((column, cellIndex) => (
        <TableCell
          key={cellIndex}
          ref={usesHandle && cellIndex === dragHandleColumnIndex ? drag : undefined}
          tabIndex={usesHandle && cellIndex === dragHandleColumnIndex && !disableReorder ? 0 : undefined}
          role={usesHandle && cellIndex === dragHandleColumnIndex ? 'button' : undefined}
          aria-label={usesHandle && cellIndex === dragHandleColumnIndex ? `Reorder ${row?.name || 'row'}` : undefined}
          onKeyDown={usesHandle && cellIndex === dragHandleColumnIndex ? (event) => {
            if (disableReorder) return;
            if (event.key === 'ArrowUp' && index > 0) {
              event.preventDefault();
              moveRow(index, index - 1);
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              moveRow(index, index + 1);
            }
          } : undefined}
          style={{width: column.width}}
        >
          {typeof column.renderCell === 'function' ? column.renderCell({ value: row[column.field], row, index }) : row[column.field]}
        </TableCell>
      ))}
    </TableRow>
  );
};

const DraggableDataGrid = ({
  data,
  columns,
  onListUpdate,
  pagination = false,
  paginationModel,
  onPaginationModelChange,
  rowsPerPageOptions = [10, 25, 50, 100],
  disableReorder = false,
  dragHandleColumnIndex = null,
}) => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    setRows(data || []);
  }, [data]);

  // const moveRow = useCallback((dragIndex, hoverIndex) => {
  //   if (dragIndex === -1 || hoverIndex === -1) {
  //     // If dragIndex or hoverIndex is -1, it means the drop is not yet completed
  //     return;
  //   }

  //   setRows((prevRows) => {
  //     const newRows = [...prevRows];
  //     const draggedRow = newRows[dragIndex];
  //     newRows.splice(dragIndex, 1);
  //     newRows.splice(hoverIndex, 0, draggedRow);
  //     const finalList = newRows.map((row, index) => ({ ...row, order: index + 1 }));
  //     onListUpdate(finalList);
  //     return finalList;
  //   });
  // }, []);
  const debouncedMoveRow = useMemo(() => debounce((dragIndex, hoverIndex) => {
      if (dragIndex < 0 || hoverIndex < 0) {
        // If dragIndex or hoverIndex is -1, it means the drop is not yet completed
        return;
      }
  
      setRows((prevRows) => {
        if (dragIndex >= prevRows.length || hoverIndex >= prevRows.length) return prevRows;
        const newRows = [...prevRows];
        const draggedRow = newRows[dragIndex];
        newRows.splice(dragIndex, 1);
        newRows.splice(hoverIndex, 0, draggedRow);
        const finalList = newRows.map((row, index) => ({ ...row, order: index + 1 }));
        onListUpdate(finalList);
        return finalList;
      });
    }, 300), [onListUpdate]);
  useEffect(() => {
    return () => {
      debouncedMoveRow.cancel();
    };
  }, [debouncedMoveRow]);
  const moveRow = useCallback((dragIndex, hoverIndex) => {
    debouncedMoveRow(dragIndex, hoverIndex);
  }, [debouncedMoveRow]);
  const page = paginationModel?.page || 0;
  const pageSize = paginationModel?.pageSize || 10;
  const totalRows = rows.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const startIndex = safePage * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRows);
  const visibleRows = pagination ? rows.slice(startIndex, endIndex) : rows;
  const from = totalRows === 0 ? 0 : startIndex + 1;
  const to = pagination ? endIndex : totalRows;

  return (
    <DndProvider backend={HTML5Backend}>
      <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: '10px' }}>
        <StyledTable>
          <TableHead>
            <TableRow>
              {columns.map((column, columnIndex) => (
                <TableCell key={columnIndex} style={{width: column.width, fontWeight: column?.headerFontWeight, fontSize: column?.headerFontSize, lineHeight: column?.headerLineHeight}}>{column.headerName}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => (
              <DraggableTableRow
                key={row?.id || `${safePage}-${index}`}
                row={row}
                index={pagination ? startIndex + index : index}
                moveRow={moveRow}
                columns={columns}
                disableReorder={disableReorder}
                dragHandleColumnIndex={dragHandleColumnIndex}
              />
            ))}
          </TableBody>
        </StyledTable>
        {!rows?.length && <NoDataFound />}
        {pagination && (
          <Box
            sx={{
              mt: 1,
              px: 1,
              py: 0.8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderTop: '1px solid #f0ede8',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontSize: '12px', color: '#65605a', fontWeight: 600 }}>
              Showing {from} to {to} of {totalRows} entries
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Typography sx={{ fontSize: '12px', color: '#65605a', fontWeight: 600 }}>Rows per page:</Typography>
                <Select
                  value={pageSize}
                  size="small"
                  onChange={(e) =>
                    onPaginationModelChange?.({
                      page: 0,
                      pageSize: Number(e.target.value),
                    })
                  }
                  sx={{ minWidth: 72, height: 30, fontSize: '12px' }}
                >
                  {rowsPerPageOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
              <Pagination
                color="primary"
                page={safePage + 1}
                count={pageCount}
                onChange={(event, value) => onPaginationModelChange?.({ page: value - 1, pageSize })}
                shape="rounded"
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '13px',
                    minWidth: 30,
                    height: 30,
                    color: '#6f6a63',
                  },
                  '& .Mui-selected': {
                    backgroundColor: '#1976d2 !important',
                    color: '#fff',
                    fontWeight: 600,
                  },
                }}
              />
            </Stack>
          </Box>
        )}
      </TableContainer>
    </DndProvider>
  );
};

export default DraggableDataGrid;

